import {
  createHostEmbedClient,
  HostEmbedClient,
} from '@sparemin/embed-service-messaging';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import EmbedPreview, { EmbedPreviewProps } from 'components/EmbedPreview';
import {
  EMBED_PREVIEW_MESSAGING_DEBUG,
  SPAREMIN_SERVICES_EMBED_URL,
} from 'config';
import {
  registerPreview,
  unregisterPreview,
} from 'redux/middleware/embed-preview-sync';
import {
  aspectRatioSelector,
  embedUrlSelector,
} from 'redux/modules/embed/selectors';
import { embedLoadMessageSelector } from './selectors';

export type AdvancedEditorPreviewProps = Omit<
  EmbedPreviewProps,
  'aspectRatio' | 'src'
>;

const AdvancedEditorPreview: React.FC<AdvancedEditorPreviewProps> = ({
  onLoad,
  ...props
}) => {
  const clientRef = useRef<HostEmbedClient>(null);
  const aspectRatio = useSelector(aspectRatioSelector);

  // once the preview loads, we communicate updates via the messagaing sdk.  the
  // url of the iframe should not change on each config update
  const [src] = useState(useSelector(embedUrlSelector));

  const handleLoad = useCallback(
    (el: HTMLIFrameElement) => {
      clientRef.current?.destroy();

      const client = createHostEmbedClient(el, {
        allowedOrigin: SPAREMIN_SERVICES_EMBED_URL,
        debug: EMBED_PREVIEW_MESSAGING_DEBUG,
        targetOrigin: SPAREMIN_SERVICES_EMBED_URL,
      });

      clientRef.current = client;

      registerPreview({
        send: client.config.send,
        buildMessage: getState => embedLoadMessageSelector(getState()),
      });

      onLoad?.(el);
    },
    [onLoad],
  );

  useEffect(
    () => () => {
      if (clientRef.current) {
        unregisterPreview();
        clientRef.current.destroy();
      }
    },
    [],
  );

  return (
    <EmbedPreview {...props} {...{ aspectRatio, src }} onLoad={handleLoad} />
  );
};

export default AdvancedEditorPreview;
